import { useState, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { objectsToCsvString } from '../../../utils/csv';
import { useCsvEditorContext } from '../contexts/CsvEditorContext';
import { useRowState } from './useRowState';

/**
 * Hook for handling CSV save operations.
 *
 * NOTE: Adding new rows is NOT supported. Only editing existing rows is allowed.
 * The save logic only processes rows that exist in originalRows. If any new rows
 * are detected, the save operation will be aborted with an error message.
 *
 * Uses context for data state and useRowState for change detection.
 */
export const useCsvSave = (uploadCsvString, syncBqToPgNames) => {
  const [isSaving, setIsSaving] = useState(false);
  const notify = useNotify();
  const {
    rows,
    originalRows,
    originalColumnOrder,
    originalRowsMap,
    setRows,
    setOriginalRows
  } = useCsvEditorContext();
  const { isRowChangedById } = useRowState();

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      // Detect any new rows (rows not in originalRows)
      const newRows = rows.filter((row) => !originalRowsMap.has(row.id));
      if (newRows.length > 0) {
        notify(
          `לא ניתן לשמור: נמצאו ${newRows.length} שורות חדשות. הוספת שורות אינה נתמכת. אנא הסר את השורות החדשות ונסה שוב.`,
          { type: 'error' }
        );
        setIsSaving(false);
        return; // Abort save
      }

      // Create a map of current rows by ID for efficient lookup
      const currentRowsMap = new Map();
      rows.forEach((row) => {
        currentRowsMap.set(row.id, row);
      });

      // Merge original rows with changed values from current rows
      // This ensures we send ALL rows, using updated values where they exist
      const rowsToSave = originalRows.map((originalRow) => {
        const currentRow = currentRowsMap.get(originalRow.id);
        if (currentRow && isRowChangedById(currentRow.id)) {
          // Use the changed row values
          return { ...currentRow };
        }
        // Use original row if no changes
        return { ...originalRow };
      });

      // Use the original column order when converting back to CSV
      const csvString = objectsToCsvString(rowsToSave, originalColumnOrder);

      await uploadCsvString(csvString);
      // Update original rows after successful save
      setOriginalRows(rowsToSave.map((row) => ({ ...row })));
      // Also update rows to match what was saved
      setRows(rowsToSave);
      notify('...השמירה בוצעה בהצלחה. מתחיל סנכרון', { type: 'success' });
      await syncBqToPgNames();
      notify('הסנכרון בוצע בהצלחה', { type: 'success' });
    } catch (error) {
      console.error('Error saving CSV:', error);
      notify('שגיאה בשמירת הקובץ', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [
    rows,
    originalRows,
    originalColumnOrder,
    originalRowsMap,
    setRows,
    setOriginalRows,
    isRowChangedById,
    uploadCsvString,
    syncBqToPgNames,
    notify
  ]);

  return {
    isSaving,
    handleSave
  };
};
